import { WebviewActionHideKeyboard } from '@/utils/action';
import { FormattedMessage, useIntl } from 'react-intl';
import Input from '../Input';
import { useQueryClient } from '@tanstack/react-query';
import useApi from '@/hooks/useApi';
import { getCart } from '@/apis/main';
import { useEffect, useRef, useState } from 'react';

interface IProps {
  onClose?: () => void;
  onClick?: () => void;
  subtotal: number;
  setCouponCode: React.Dispatch<React.SetStateAction<string>>;
}

const PromotionCodeFormModal = ({ onClose, setCouponCode }: IProps) => {
  const intl = useIntl();
  const { fetchApi } = useApi();
  const queryClient = useQueryClient();
  const [couponCodeState, setCouponCodeState] = useState('');
  const [errorMessage, setErrormessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClickSubmitButton = async () => {
    try {
      await fetchApi((accessToken) => getCart(accessToken, couponCodeState));
    } catch (error) {
      const ERROR = error as { code?: string };
      if (ERROR?.code === 'INVALID_COUPON_CODE') {
        return setErrormessage(
          intl.formatMessage({ id: 'the_code_you_entered_is_invalid', defaultMessage: 'The code you entered is invalid.' })
        );
      }
      if (ERROR?.code === 'EXPIRED_COUPON_CODE') {
        return setErrormessage(intl.formatMessage({ id: 'this_code_has_expired', defaultMessage: 'This code has expired.' }));
      }
      if (ERROR?.code === 'COUPON_ALREADY_BEEN_USED') {
        return setErrormessage(
          intl.formatMessage({ id: 'this_code_has_already_been_used', defaultMessage: 'This code has already been used.' })
        );
      }
      if (ERROR?.code === 'COUPON_CONDITIONS_NOT_MET') {
        return setErrormessage(
          intl.formatMessage({
            id: 'this_code_doesn’t_meet_the_required_conditions',
            defaultMessage: 'This code doesn’t meet the required conditions.'
          })
        );
      } else {
        return setErrormessage(ERROR?.code ?? '');
      }
    }
    setCouponCode(couponCodeState);
    queryClient.invalidateQueries({ queryKey: ['cart'] });
    onClose?.();
    WebviewActionHideKeyboard();
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 w-full h-full bg-black/80 flex justify-center items-start z-[1000]">
      <div className="min-w-[570px] mt-[140px] rounded-2xl bg-white text-center p-10 pt-12 flex flex-col gap-10">
        <h3 className="text-[30px] leading-9 font-extrabold">
          <FormattedMessage id="enter_your_promo_code" defaultMessage="Enter your promo code" />
        </h3>
        <form
          className="relative"
          onSubmit={(e) => {
            e.preventDefault();
            handleClickSubmitButton();
          }}
        >
          <Input
            ref={inputRef}
            type="text"
            containerClassName="w-full"
            errorMessage={errorMessage}
            placeholder={intl.formatMessage({ id: 'not_case_sensitive', defaultMessage: 'Not case-sensitive' })}
            value={couponCodeState}
            onChange={(e) => setCouponCodeState(e.target.value)}
          />
          {couponCodeState && (
            <img
              src="/icons/huge.svg"
              className="absolute top-[15px] right-5 cursor-pointer"
              alt="clear-input"
              onClick={() => setCouponCodeState('')}
            />
          )}
        </form>

        <div className="flex gap-[12px]">
          <button
            className="flex-1 flex justify-center items-center h-[66px] rounded-[20px] bg-[#f2f2f2] text-[18px] font-bold data-[primary=true]:bg-[#ff6060] data-[primary=true]:text-white"
            onClick={() => onClose?.()}
          >
            <FormattedMessage id="back" defaultMessage="Back" />
          </button>
          <button
            className="flex-1 flex justify-center items-center h-[66px] rounded-[20px] text-[18px] font-bold bg-[#f2f2f2] disabled:bg-[#FFCCCC] data-[primary=true]:bg-[#ff6060] data-[primary=true]:text-white"
            onClick={handleClickSubmitButton}
            disabled={!couponCodeState}
            data-primary={true}
          >
            <FormattedMessage id="enter" defaultMessage="Enter" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionCodeFormModal;
